import { useRootContext } from 'app/contexts/reports'
import { AgendaReportStatus } from 'services/http/report/agenda-report/enums'
import { AgendaReportList } from 'services/http/report/agenda-report-4'
import {
  AGENDA_REPORT_STATUSES,
  AGENDA_REPORT_INDEPENDENT_STATUSES,
} from './../../constant'
import { IColumnLinkButtonsProps, IDesignerLinkButtonsProps } from './types'

import { LinkButton } from '@zera-admin/button'
import Button from '@zera-admin/button'
import Lozenge from '@zera-admin/lozenge'
import Tooltip from 'components/Tooltip'
import { useState } from 'react'
import Icon from '@zera-admin/icon'
import agendaReportService from 'services/http/report/agenda-report-4'

const ColumnLinkButtons = ({ groupId }: IColumnLinkButtonsProps) => {
  const { setWithBots } = useRootContext()

  return (
    <>
      <LinkButton
        appearance="subtle-link"
        onClick={() => setWithBots(false)}
        href={`/agenda-reportv4/detail/${groupId}?status=${1}`}
      >
        Görüntüle
      </LinkButton>
    </>
  )
}

const DesignerLinkButtons = ({ groupId }: IDesignerLinkButtonsProps) => {
  const { setWithBots } = useRootContext()

  return (
    <>
      <LinkButton
        appearance="subtle-link"
        href={`/agenda-reportv4/detail/${groupId}?status=2`}
      >
        Düzenle
      </LinkButton>
    </>
  )
}

const ExcelReportLinks = ({ urls }: { urls: string[] }) => {
  const [isOpen, setIsOpen] = useState(false)

  if (!urls || urls.length === 0) return null

  const getFileName = (url: string) => {
    // URL'deki son / karakterinden sonraki kısmı al
    const parts = url.split('/')
    return parts[parts.length - 1] || 'Excel Rapor'
  }

  const getFullUrl = (url: string) => {
    // URL'nin başına https:// ekle (eğer yoksa)
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    return `https://${url}`
  }

  const handleClickOutside = (event: React.MouseEvent) => {
    // Popup dışına tıklandığında kapat
    if (event.target === event.currentTarget) {
      setIsOpen(false)
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <LinkButton
        appearance="subtle-link"
        onClick={() => setIsOpen(!isOpen)}
      >
        Excel Raporlar ({urls.length})
      </LinkButton>
      
      {isOpen && (
        <>
          {/* Overlay - popup dışına tıklamak için */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={handleClickOutside}
          />
          
          {/* Popup - butonun altında */}
          <div style={{
            position: 'absolute',
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '10px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            zIndex: 1000,
            minWidth: '200px',
            top: '100%',
            left: '0',
            marginTop: '5px'
          }}>
            {urls.map((url, index) => (
              <div key={index} style={{ marginBottom: '8px' }}>
                <a 
                  href={getFullUrl(url)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#0066cc', 
                    textDecoration: 'none',
                    display: 'block',
                    padding: '4px 8px',
                    borderRadius: '3px',
                    border: '1px solid #e0e0e0'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5'
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                >
                  {getFileName(url)}
                </a>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const handleStopTask = async (groupId: string) => {
  try {
    await agendaReportService.stopAgendaTaskV4(groupId)
    
    // Refresh the page to show updated status
    window.location.reload()
  } catch (error) {
    console.error('Error stopping task:', error)
  }
}

export const tableColumns = [
  {
    name: 'reportName',
    text: 'Rapor adı',
  },
  {
    name: 'createdAtStr',
    text: 'Rapor istek tarihi',
    render: (props: any) => {
      return new Date(props.createdAt).toLocaleString()
    },
  },
  {
    name: 'crawlingEndedAt',
    text: 'Rapor tamamlanma tarihi',
    render: (props: any) => {
      if (!props.crawlingEndedAt) {
        return '-'
      }

      return new Date(props.crawlingEndedAt).toLocaleString()
    },
  },
  {
    name: 'excelReportUrls',
    text: 'Excel Rapor',
    render: (props: any) => {
      if (props.excelReportBuildFinished !== true) {
        return '-'
      }
      
      if (!props.excelReportUrls || props.excelReportUrls.length === 0) {
        return '-'
      }
      
      const validUrls = props.excelReportUrls.filter((url: string | null) => url !== null)
      if (validUrls.length === 0) {
        return '-'
      }
      
      return <ExcelReportLinks urls={validUrls} />
    },
  },
  {
    name: 'status',
    text: 'Durumu',
    render: (props: any) => {
      if (props.paused === true) {
        return <Lozenge appearance="error">Durduruldu</Lozenge>
      }

      if (props.status === AgendaReportStatus.Completed) {
        return <Lozenge appearance="success">Tamamlandı</Lozenge>
      }

      if (props.status === AgendaReportStatus.Error) {
        return (
          <Tooltip content={props?.lastError || ''}>
            <Lozenge appearance="error">Hata Oluştu</Lozenge>
          </Tooltip>
        )
      }

      const status =
        props.pathId === 'agenda-report'
          ? AGENDA_REPORT_STATUSES.find(
              (status) => status.value === props.status
            )
          : AGENDA_REPORT_INDEPENDENT_STATUSES.find(
              (status) => status.value === props.status
            )

      return (
        <>
          <Lozenge appearance="warning">
            {`${props?.progressPercentage}% `}
          </Lozenge>
          <Lozenge appearance="warning">{status?.label}</Lozenge>
        </>
      )
    },
  },
  {
    name: 'stopTask',
    text: 'Durdur',
    width: '100px',
    render: (item: AgendaReportList) => {
      if (!item.groupId) return null
      if (item.paused === true) return null
      if (item.status === AgendaReportStatus.Completed) return null
      if (item.status === AgendaReportStatus.Error) return null

             return (
         <Button
           appearance="error"
           size="small"
           onClick={() => handleStopTask(item.groupId)}
           iconBefore={<Icon name="vid-pause" size="small" />}
           style={{ 
             padding: '4px 8px',
             fontSize: '12px',
             minHeight: '24px',
             lineHeight: '1'
           }}
         >
           Durdur
         </Button>
       )
    },
  },
  {
    name: 'operation',
    text: 'Task ',
    width: '100px',
    render: (item: AgendaReportList) => {
      if (!item.groupId) return null

      return (
        <>
          <LinkButton
            appearance="subtle-link"
            href={`/agenda-reportv4/task-detail/${item.groupId}`}
          >
            Görüntüle
          </LinkButton>
        </>
      )
    },
  },
  {
    name: 'operation',
    text: 'Rapor',
    width: '100px',
    render: (item: AgendaReportList) => {
      if (!item.groupId) return null
      if (item?.status === AgendaReportStatus.Error) return null
      if (item.paused === true) return null
      if (item.status !== AgendaReportStatus.Completed) return null
      
      return (
        <>
          <ColumnLinkButtons groupId={item.groupId} />
          <DesignerLinkButtons groupId={item.groupId} />
        </>
      )
    },
  },
]
